import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Optimistically load cached user profile so UI renders instantly
    const cached = localStorage.getItem("cached_user_profile");
    if (cached) {
      try {
        setUser(JSON.parse(cached));
        setLoading(false);
      } catch (e) {
        localStorage.removeItem("cached_user_profile");
      }
    }

    try {
      const devToken = localStorage.getItem("dev_bypass_token");
      if (!devToken) {
        // Add a timeout to prevent infinite hanging when the app wakes up
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error("Supabase session timeout")), 8000)
        );
        const { data: sessionData, error: sessionError } = await Promise.race([sessionPromise, timeoutPromise]);
        
        if (sessionError || !sessionData?.session) {
          setUser(null);
          localStorage.removeItem("cached_user_profile");
          supabase.auth.signOut().catch(() => {});
          setLoading(false);
          return;
        }
      }

      // Fetch latest profile from backend
      const { data } = await api.get("/auth/me");
      localStorage.setItem("cached_user_profile", JSON.stringify(data));
      setUser(data);
    } catch (err) {
      console.error("Auth check error:", err);
      // If 401 Unauthorized, session is dead, must log out
      if (err.response && err.response.status === 401) {
        setUser(null);
        localStorage.removeItem("cached_user_profile");
      } else {
        // Network error/timeout. Keep cached user if available
        if (!localStorage.getItem("cached_user_profile")) {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setUser(null);
        localStorage.removeItem("cached_user_profile");
        setLoading(false);
      } else if (event !== "INITIAL_SESSION") {
        checkAuth();
      }
    });
    return () => listener.subscription.unsubscribe();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("dev_bypass_token");
    localStorage.removeItem("cached_user_profile");
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
